import classNames from 'classnames';
import * as _ from 'lodash-es';
import React from 'react';
import SidebarRow, { SidebarRowRightContent } from './SidebarRow';
import * as styles from './SidebarSection.module.scss';
import SidebarSectionAnimation from './SidebarSectionAnimation';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { UIActions } from '../ui/uiSlice';

export enum CollapseBehavior {
  InitiallyCollapsed = 'initiallyCollapsed',
  InitiallyExpanded = 'initiallyExpanded',
  AlwaysExpanded = 'alwaysExpanded',
}

interface SidebarSectionProps {
  children?: React.ReactNode;
  id: string;
  title: string;
  collapseBehavior: CollapseBehavior;
  icon?: React.ReactNode;
  menuItems?: React.ReactNode;
  customMenu?: React.ReactNode;
  publicVariant?: boolean;
}

const SidebarSection = ({
  children,
  id,
  title,
  menuItems,
  icon,
  customMenu,
  collapseBehavior,
  publicVariant,
}: SidebarSectionProps) => {
  const dispatch = useAppDispatch();
  const collapsedSidebarSections = useAppSelector(
    (state) => state.ui.collapsedSidebarSections,
  );

  const isAlwaysExpanded = collapseBehavior === CollapseBehavior.AlwaysExpanded;
  const isExpanded =
    isAlwaysExpanded ||
    (id in collapsedSidebarSections
      ? !collapsedSidebarSections[id]
      : collapseBehavior === CollapseBehavior.InitiallyExpanded);

  const handleExpandClick: React.MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(
      UIActions.toggleSidebarSection({
        section: id,
        isInitiallyCollapsed:
          collapseBehavior === CollapseBehavior.InitiallyCollapsed,
      }),
    );
  };

  const getExpandState = () => {
    if (isAlwaysExpanded) return 'none';
    return isExpanded ? 'expanded' : 'collapsed';
  };

  return (
    <div className={styles.container} aria-label={_.startCase(title)}>
      <div>
        <SidebarRow
          icon={icon}
          onClick={isAlwaysExpanded ? undefined : handleExpandClick}
          rightContent={
            <SidebarRowRightContent
              menuItems={menuItems}
              customMenu={customMenu}
              expandState={getExpandState()}
              onExpandClick={handleExpandClick}
            />
          }
        >
          <span
            className={classNames(styles.title, {
              [styles.isPublicVariant]: publicVariant,
            })}
          >
            {title}
          </span>
        </SidebarRow>
      </div>
      <SidebarSectionAnimation sectionKey={id} expanded={isExpanded}>
        {children}
      </SidebarSectionAnimation>
    </div>
  );
};

export default SidebarSection;
